import { useEffect, useMemo, useState } from "react";
import { useAuthOptional } from "@/contexts/AuthContext";
import { requireSupabase } from "@/lib/supabase";
import { SectionCard } from "@/components/common/SectionCard";
import { ActivityTimelineList } from "@/components/activity/ActivityTimelineList";
import {
  FORENSICS_PAGE_SIZE_OPTIONS,
} from "@/lib/activityTimeline/forensicsPaging";
import type { OrgPickRow } from "./devPanelTypes";
import { isUuid } from "./devPanelShared";
import { DevActivityForensicsFilters } from "./DevActivityForensicsFilters";
import { useActivityTimelineForensics } from "./useActivityTimelineForensics";

const PLATFORM_FORENSICS_ROLES = ["platform_owner", "platform_admin", "platform_dev"] as const;

export function DevActivityForensicsPanel() {
  const auth = useAuthOptional();
  const canAccess =
    auth?.status === "ready" &&
    auth.realHasRole(...PLATFORM_FORENSICS_ROLES);

  const sb = useMemo(() => {
    try {
      return requireSupabase();
    } catch {
      return null;
    }
  }, []);

  const [orgs, setOrgs] = useState<OrgPickRow[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState("");

  useEffect(() => {
    if (!canAccess || !auth?.session || !sb) {
      setOrgs([]);
      setOrgsError(null);
      return;
    }
    let cancelled = false;
    setOrgsLoading(true);
    setOrgsError(null);
    void (async () => {
      try {
        const { data, error } = await sb.rpc("platform_list_orgs");
        if (cancelled) return;
        if (error) {
          setOrgsError(error.message || String(error));
          setOrgs([]);
          return;
        }
        const rows = ((data as { id?: string; name?: string }[]) || [])
          .map((r) => ({
            id: String(r.id || ""),
            name: String(r.name || r.id || "").trim() || String(r.id),
          }))
          .filter((r) => r.id && isUuid(r.id))
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        setOrgs(rows);
        setOrganizationId((prev) => prev || rows[0]?.id || "");
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess, auth?.session, sb]);

  const forensics = useActivityTimelineForensics({
    enabled: canAccess && Boolean(organizationId),
    organizationId,
  });

  if (!canAccess) return null;

  return (
    <SectionCard
      defaultCollapsed
      title="Activity forensics"
      description="Cross-org federated timeline (platform_dev / platform_admin / platform_owner). scope=platform via OAT-1 API."
    >
      <DevActivityForensicsFilters
        organizationId={organizationId}
        onOrganizationId={setOrganizationId}
        orgs={orgs}
        orgsLoading={orgsLoading}
        filters={forensics.filters}
        patchFilters={forensics.patchFilters}
        working={forensics.working}
        onRefresh={() => void forensics.refresh()}
        onReset={forensics.resetFilters}
      />

      {orgsError ? (
        <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {orgsError}
        </div>
      ) : null}
      {forensics.error ? (
        <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {forensics.error}
        </div>
      ) : null}

      {forensics.federationTruncated ? (
        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Per-source fetch cap hit — results may be truncated. Narrow the date range or add filters.
        </div>
      ) : null}

      {forensics.sourcesUnavailable.length > 0 ? (
        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800">
          Skipped unavailable sources (DDL not applied yet): {forensics.sourcesUnavailable.join(", ")}
        </div>
      ) : null}

      <div className="mt-3">
        {forensics.sourcesQueried.length > 0 && forensics.entries.length > 0 ? (
          <div className="mb-2 text-xs text-gray-500 dark:text-zinc-400">
            Sources: {forensics.sourcesQueried.join(", ")}
          </div>
        ) : null}
        <ActivityTimelineList
          entries={forensics.entries}
          total={forensics.total}
          loading={forensics.loading}
          error={null}
          scope="platform"
          variant="forensics"
          working={forensics.working}
          page={forensics.page}
          pageCount={forensics.pageCount}
          pageSize={forensics.pageSize}
          pageSizeOptions={[...FORENSICS_PAGE_SIZE_OPTIONS]}
          onPageChange={forensics.setPage}
          onPageSizeChange={forensics.setPageSize}
          onDrillEntity={forensics.enterEntityDrill}
          drillEntityId={forensics.filters.entityId.trim() || null}
          onClearDrill={forensics.clearEntityDrill}
          emptyMessage={
            organizationId ? "No timeline entries match these filters." : "Select an organization."
          }
        />
      </div>
    </SectionCard>
  );
}
